import {
  type DefaultFoundationTheme,
  FoundationProvider,
  type FoundationThemeOverride,
  FoundationThemeContext,
  useFoundationTheme,
} from 'platform/foundation';
import {Currency, LocaleConfig, LocaleProvider} from 'platform/locale';
// eslint-disable-next-line no-restricted-imports -- coexistence bridge: styled ThemeProvider for app styled() call sites until they migrate off styled-components; approved by Martin 2026-10-12
import {type DefaultTheme, ThemeProvider} from 'styled-components';
import {DeepPartial} from 'utility-types';

import {ReactNode, useMemo} from 'react';

import {mergeDeepRight} from 'ramda';

import {ComponentsThemeContextProvider} from '../../hooks/useComponentsTheme';
import '../../styles/reset.css';
import {componentsTheme, DefaultComponentsTheme} from '../../utils/componentsTheme';
import {DialogsProvider} from '../Dialog/components/DialogsProvider';
import {DropdownProvider} from '../Dropdown/components/DropdownProvider';
import {NotificationsProvider} from '../Notification/components/NotificationsProvider';
import {
  TranslationProvider,
  TranslationInterpreter,
} from '../TranslationProvider/TranslationProvider';

type ComponentsThemeOverride = Omit<FoundationThemeOverride, 'icons' | 'lotties'> &
  DeepPartial<Pick<DefaultComponentsTheme, 'components' | 'config' | 'styles'>> & {
    icons?: FoundationThemeOverride['icons'];
    lotties?: FoundationThemeOverride['lotties'];
  };

type FoundationComponentsTheme = Omit<DefaultComponentsTheme, 'icons' | 'lotties'> &
  Pick<DefaultFoundationTheme, 'icons' | 'lotties'>;

type MergedComponentsThemes = {
  foundation: FoundationComponentsTheme;
  components: DefaultComponentsTheme;
};

type ComponentsProviderProps = {
  language: string;
  children?: ReactNode;
  localeConfig?: LocaleConfig;
  theme?: ComponentsThemeOverride;
  currencies?: Currency[];
  t?: TranslationInterpreter;
  debug?: boolean;
};

type ComponentsThemeBridgeProps = Pick<ComponentsProviderProps, 'children' | 'theme'>;

function ComponentsThemeBridge(props: ComponentsThemeBridgeProps) {
  const foundationTheme = useFoundationTheme();
  const mergedThemes = useMemo<MergedComponentsThemes>(() => {
    const {
      icons: foundationIcons,
      lotties: foundationLotties,
      ...foundationThemeValues
    } = foundationTheme;
    const {icons: componentIcons, lotties: componentLotties, ...componentTheme} = componentsTheme;
    const {icons: overrideIcons, lotties: overrideLotties, ...themeOverrides} = props.theme ?? {};
    const foundationThemeToMerge: object = foundationThemeValues;
    const themeOverridesToMerge: object = themeOverrides;
    const mergedThemeValues = mergeDeepRight(
      mergeDeepRight(foundationThemeToMerge, componentTheme),
      themeOverridesToMerge
    );
    const icons = {...foundationIcons, ...componentIcons, ...overrideIcons};
    const lotties = {...foundationLotties, ...componentLotties, ...overrideLotties};

    return {
      foundation: {...mergedThemeValues, icons, lotties},
      components: {
        ...mergedThemeValues,
        icons: {
          ...icons,
          fallback: typeof icons.fallback === 'string' ? componentIcons.fallback : icons.fallback,
        },
        lotties,
      },
    };
  }, [foundationTheme, props.theme]);

  return (
    // Coexistence bridge for legacy styled-components consumers; remove after their migration.
    // Turn off because of chakra theme - remove when chakra is gone
    // eslint-disable-next-line eslint-js/no-restricted-syntax -- ported legacy coexistence-bridge assertion; approved by Martin 2026-10-12, removed with the bridge
    <ThemeProvider theme={mergedThemes.foundation as unknown as DefaultTheme}>
      <FoundationThemeContext.Provider value={mergedThemes.foundation}>
        <ComponentsThemeContextProvider value={mergedThemes.components}>
          {props.children}
        </ComponentsThemeContextProvider>
      </FoundationThemeContext.Provider>
    </ThemeProvider>
  );
}

export function ComponentsProvider(props: ComponentsProviderProps) {
  return (
    <FoundationProvider theme={props.theme}>
      <ComponentsThemeBridge theme={props.theme}>
        <TranslationProvider t={props.t}>
          <LocaleProvider
            currencies={props.currencies}
            language={props.language}
            config={props.localeConfig}
          >
            <NotificationsProvider debug={props.debug}>
              <DialogsProvider>
                <DropdownProvider>{props.children}</DropdownProvider>
              </DialogsProvider>
            </NotificationsProvider>
          </LocaleProvider>
        </TranslationProvider>
      </ComponentsThemeBridge>
    </FoundationProvider>
  );
}
